import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { TrendingUp, Star, Target, Award, ChevronRight, IndianRupee } from "lucide-react";
import RadarChart from "@/components/RadarChart";

function money(n) { return "₹ " + Number(n || 0).toLocaleString("en-IN"); }
function fmt(d) { try { return new Date(d).toLocaleDateString("en-IN"); } catch { return d; } }

const STATUS_STYLE = {
  Draft: "bg-slate-100 text-slate-700",
  "Self Complete": "bg-red-50 text-red-700",
  "Manager Complete": "bg-orange-50 text-orange-700",
  Finalized: "bg-emerald-50 text-emerald-700",
};

export default function Performance() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [cycles, setCycles] = useState([]);
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [activeReview, setActiveReview] = useState(null); // {mode: 'self'|'manager'|'finalize', review}
  const [selfKras, setSelfKras] = useState([]);
  const [selfComment, setSelfComment] = useState("");
  const [mgrKras, setMgrKras] = useState([]);
  const [mgrComment, setMgrComment] = useState("");
  const [hike, setHike] = useState(0);
  const [overall, setOverall] = useState(3.5);
  const [showNewCycle, setShowNewCycle] = useState(false);
  const [newCycle, setNewCycle] = useState({ name:"", quarter:"Q2", year: new Date().getFullYear(), start_date:"", end_date:"" });
  const [selectedCycle, setSelectedCycle] = useState("");

  const load = async () => {
    const c = await api.get("/performance/cycles");
    setCycles(c.data);
    if (c.data[0]) setSelectedCycle(c.data[0].id);
    if (user?.employee_id) setMine((await api.get("/performance/reviews/mine")).data);
    if (isReviewer) setAll((await api.get("/performance/reviews")).data);
  };
  useEffect(() => { load(); }, [user?.employee_id, isReviewer]);

  const source = isReviewer ? all : mine;
  const filtered = selectedCycle ? source.filter(r => r.cycle_id === selectedCycle) : source;

  const openSelf = (r) => {
    setActiveReview({ mode: "self", review: r });
    setSelfKras(r.kras.map(k => ({ ...k })));
    setSelfComment(r.self_comment || "");
  };
  const openMgr = (r) => {
    setActiveReview({ mode: "manager", review: r });
    setMgrKras(r.kras.map(k => ({ ...k })));
    setMgrComment(r.manager_comment || "");
  };
  const openFinalize = (r) => {
    setActiveReview({ mode: "finalize", review: r });
    setHike(r.hike_percent || 10);
    setOverall(r.overall_rating || avgRating(r.kras));
  };

  const submitSelf = async () => {
    try {
      await api.put(`/performance/reviews/${activeReview.review.id}/self`, { kras: selfKras, self_comment: selfComment });
      toast.success("Self-review submitted");
      setActiveReview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitMgr = async () => {
    try {
      await api.put(`/performance/reviews/${activeReview.review.id}/manager`, { kras: mgrKras, manager_comment: mgrComment });
      toast.success("Manager review submitted");
      setActiveReview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitFinal = async () => {
    try {
      await api.put(`/performance/reviews/${activeReview.review.id}/finalize`, { hike_percent: Number(hike), overall_rating: Number(overall) });
      toast.success("Review finalized");
      setActiveReview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createCycle = async (e) => {
    e.preventDefault();
    try {
      await api.post("/performance/cycles", { ...newCycle, year: Number(newCycle.year) });
      toast.success("Cycle created & reviews generated");
      setShowNewCycle(false);
      setNewCycle({ name:"", quarter:"Q2", year: new Date().getFullYear(), start_date:"", end_date:"" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="perf-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Performance</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Reviews that lead to raises.</h1>
          <p className="mt-1 text-sm text-slate-500">KPIs, KRAs and appraisal cycles tied to CTC decisions.</p>
        </div>
        {isReviewer && (
          <Button onClick={() => setShowNewCycle(v => !v)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="new-cycle-btn">
            {showNewCycle ? "Close" : "New cycle"}
          </Button>
        )}
      </div>

      {showNewCycle && (
        <Card className="p-6 rounded-xl border-slate-200/60" data-testid="cycle-form-card">
          <form onSubmit={createCycle} className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Cycle name (e.g. Q2 2026)</Label>
              <Input required value={newCycle.name} onChange={e => setNewCycle({...newCycle, name: e.target.value})} data-testid="cycle-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Quarter</Label>
              <Select value={newCycle.quarter} onValueChange={v => setNewCycle({...newCycle, quarter: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Q1","Q2","Q3","Q4"].map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Start</Label>
              <Input type="date" value={newCycle.start_date} onChange={e => setNewCycle({...newCycle, start_date: e.target.value})} required />
            </div>
            <div className="space-y-1.5">
              <Label>End</Label>
              <Input type="date" value={newCycle.end_date} onChange={e => setNewCycle({...newCycle, end_date: e.target.value})} required />
            </div>
            <div className="md:col-span-5 flex justify-end">
              <Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="cycle-submit">Create cycle</Button>
            </div>
          </form>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Select value={selectedCycle} onValueChange={setSelectedCycle}>
          <SelectTrigger className="w-64 h-10 bg-white" data-testid="cycle-filter">
            <SelectValue placeholder="Pick cycle" />
          </SelectTrigger>
          <SelectContent>
            {cycles.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.status}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500">{filtered.length} review(s)</div>
      </div>

      <div className="space-y-3">
        {filtered.map(r => (
          <Card key={r.id} className="p-5 rounded-xl border-slate-200/60" data-testid={`review-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-slate-900">{isReviewer ? r.employee_name : r.cycle_name}</div>
                  <Badge className={`border-0 ${STATUS_STYLE[r.status]}`}>{r.status}</Badge>
                  {r.status === "Finalized" && (
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Star className="h-3 w-3 text-orange-500" strokeWidth={2} /> {r.overall_rating} · +{r.hike_percent}% · New CTC {money(r.new_ctc)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{r.cycle_name} · {r.department} · {r.kras.length} KRAs</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {r.kras.slice(0,3).map((k) => (
                    <span key={`${r.id}-${k.title}`} className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">
                      {k.title} · {k.weight}%
                    </span>
                  ))}
                  {r.kras.length > 3 && <span className="text-[11px] text-slate-500">+ {r.kras.length - 3} more</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!isReviewer && r.status === "Draft" && (
                  <Button size="sm" className="bg-[#DC2626] hover:bg-[#B91C1C]" onClick={() => openSelf(r)} data-testid={`self-${r.id}`}>Fill self-review</Button>
                )}
                {!isReviewer && r.status !== "Draft" && (
                  <Button size="sm" variant="outline" onClick={() => openSelf(r)} data-testid={`view-${r.id}`}>View</Button>
                )}
                {isReviewer && r.status === "Self Complete" && (
                  <Button size="sm" className="bg-[#DC2626] hover:bg-[#B91C1C]" onClick={() => openMgr(r)} data-testid={`mgr-${r.id}`}>Manager review</Button>
                )}
                {isReviewer && r.status === "Manager Complete" && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openFinalize(r)} data-testid={`finalize-${r.id}`}>Finalize & set hike</Button>
                )}
                {isReviewer && (r.status === "Draft" || r.status === "Finalized") && (
                  <Button size="sm" variant="outline" onClick={() => openMgr(r)} data-testid={`open-${r.id}`}>Open</Button>
                )}
              </div>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <div className="text-sm text-slate-500 py-10 text-center border border-dashed rounded-xl">No reviews for this cycle.</div>}
      </div>

      {/* Self review dialog */}
      <Dialog open={activeReview?.mode === "self"} onOpenChange={(o) => !o && setActiveReview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Self review · {activeReview?.review?.cycle_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {selfKras.map((k, i) => (
              <KraEditor key={k.title || `kra-${i}`} k={k} readOnly={activeReview?.review?.status !== "Draft"} field="self_rating" onChange={(patch) => {
                const c = [...selfKras]; c[i] = { ...c[i], ...patch }; setSelfKras(c);
              }} />
            ))}
            <div>
              <Label>Your comments</Label>
              <Textarea rows={3} value={selfComment} onChange={e => setSelfComment(e.target.value)} disabled={activeReview?.review?.status !== "Draft"} data-testid="self-comment" />
            </div>
            {activeReview?.review?.status === "Draft" && (
              <div className="flex justify-end"><Button onClick={submitSelf} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="submit-self">Submit for manager review</Button></div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manager review dialog */}
      <Dialog open={activeReview?.mode === "manager"} onOpenChange={(o) => !o && setActiveReview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manager review · {activeReview?.review?.employee_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {mgrKras.length > 0 && (
              <div className="rounded-xl border border-slate-200 p-4 bg-white flex flex-col md:flex-row gap-4 items-center" data-testid="radar-mgr">
                <div className="flex-1 max-w-sm">
                  <RadarChart
                    axes={mgrKras.map(k => ({ label: k.title.length > 14 ? k.title.slice(0,14)+"…" : k.title }))}
                    series={[
                      { name: "Self", color: "#64748B", values: mgrKras.map(k => Number(k.self_rating || 0)) },
                      { name: "Manager", color: "#DC2626", values: mgrKras.map(k => Number(k.mgr_rating || 0)) },
                    ]}
                    max={5}
                  />
                </div>
                <div className="text-xs space-y-2">
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-slate-500"></span>Self rating</div>
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#DC2626]"></span>Manager rating</div>
                  <div className="text-slate-500 mt-3 max-w-xs">Update manager ratings below and the chart refreshes instantly.</div>
                </div>
              </div>
            )}
            {mgrKras.map((k, i) => (
              <KraEditor key={k.title || `kra-${i}`} k={k} field="mgr_rating" showSelf onChange={(patch) => {
                const c = [...mgrKras]; c[i] = { ...c[i], ...patch }; setMgrKras(c);
              }} />
            ))}
            <div>
              <Label>Manager comments</Label>
              <Textarea rows={3} value={mgrComment} onChange={e => setMgrComment(e.target.value)} data-testid="mgr-comment" />
            </div>
            <div className="flex justify-end"><Button onClick={submitMgr} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="submit-mgr">Submit review</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finalize dialog */}
      <Dialog open={activeReview?.mode === "finalize"} onOpenChange={(o) => !o && setActiveReview(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Finalize · {activeReview?.review?.employee_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-600">Current CTC: <span className="font-semibold text-slate-900">{money(activeReview?.review?.old_ctc)}</span></div>
            <div className="space-y-1.5">
              <Label>Overall rating (1-5)</Label>
              <Input type="number" step="0.1" min="1" max="5" value={overall} onChange={e => setOverall(e.target.value)} data-testid="final-rating" />
            </div>
            <div className="space-y-1.5">
              <Label>Hike percent</Label>
              <Input type="number" step="0.5" value={hike} onChange={e => setHike(e.target.value)} data-testid="final-hike" />
            </div>
            <div className="rounded-lg bg-emerald-50 p-3 flex items-center justify-between">
              <div className="text-sm text-slate-600">New CTC</div>
              <div className="font-display text-xl font-bold text-emerald-700 flex items-center">
                <IndianRupee className="h-4 w-4" strokeWidth={2} />
                {(Number(activeReview?.review?.old_ctc || 0) * (1 + Number(hike) / 100)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="flex justify-end"><Button onClick={submitFinal} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-final">Finalize & release</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function avgRating(kras) {
  const vals = kras.map(k => k.mgr_rating || k.self_rating).filter(Boolean);
  if (!vals.length) return 3.5;
  return Number((vals.reduce((s,x)=>s+x,0) / vals.length).toFixed(1));
}

function KraEditor({ k, field, readOnly, showSelf, onChange }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">{k.title}</div>
          <div className="text-xs text-slate-500">Target: {k.target} · Weight {k.weight}%</div>
        </div>
        {showSelf && <div className="text-xs text-slate-500">Employee rated: <span className="font-semibold text-slate-900">{k.self_rating || 0}/5</span></div>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs">Achievement / evidence</Label>
          <Textarea rows={2} value={k.achievement || ""} readOnly={readOnly} onChange={e => onChange({ achievement: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">{showSelf ? "Manager" : "Self"} rating (1-5)</Label>
          <Input type="number" min="0" max="5" step="0.5" value={k[field] || 0} readOnly={readOnly} onChange={e => onChange({ [field]: Number(e.target.value) })} />
        </div>
      </div>
    </div>
  );
}
